/**
 * The third-place option — Pass 06 §3.
 *
 * WHAT CHANGED, because the old mechanic and the new one look similar and are not. 2018-II-5 was
 * implemented as a swap of resolved draft SLOTS: run the lottery, resolve everybody's slot, then
 * exchange 3rd's slot with 4th's. Tim's ruling of 24 August is that the right trades LOTTERY
 * RESULTS — the choosing position, before anybody has chosen anything. Ari may take Matt's turn.
 *
 * That is a bigger difference than it sounds. Under the old reading 3rd took a slot 4th had
 * already selected with his own list; under the new one 3rd takes 4th's PLACE IN THE QUEUE and
 * then picks from his own list at that place. The two give different answers whenever the two
 * men's preferences differ, which is almost always.
 *
 * THE RIGHT IS UNCONDITIONAL ON 3RD'S OWN DRAW. Ari can hold the 8th position and still take
 * Matt's 1st — and he can hold the 2nd and take Matt's 6th, which would be daft but is his to do.
 * Optional, one-directional, and 4th has no say and no reciprocal right.
 *
 * FOUR STATES, NOT THREE. `declined` and `lapsed` have the same effect on the order and are very
 * different things to have on the record in September, so they stay apart:
 *
 *   pending    drawn, inside the window, unanswered
 *   taken      3rd said yes. Binding.
 *   declined   3rd said no. Binding.
 *   lapsed     the deadline passed with no answer. Silence declines.
 *
 * SILENCE DECLINES, and that is load-bearing rather than tidy: §7 resolves the draft
 * automatically at 5:00 PM, which is only possible if this question has an answer by then. An
 * unanswered option that blocked resolution would put a human back on the critical path.
 */

import { etStamp } from './deadlines.js';

export type SwapState = 'pending' | 'taken' | 'declined' | 'lapsed';

/** The two answers a person can actually give. `lapsed` is what the clock does, not what he says. */
export type SwapAnswer = 'taken' | 'declined';

export interface SwapDeclaration {
  answer: SwapAnswer;
  /** When it was declared — not when it was recorded, which can be later. */
  at: string;
  /** Whose declaration it is. Always the third-place GM, even when a commissioner typed it. */
  gm: string;
  /**
   * HOW IT REACHED THE APP. Verbal declaration in a room is the norm on committee night, and a
   * commissioner recording one should leave a record as good as a tap — so the path is stored
   * rather than inferred from whether `recordedBy` happens to be set.
   */
  declaredVia: 'app' | 'verbal';
  /** The commissioner who recorded a verbal declaration. Absent when 3rd tapped it himself. */
  recordedBy?: string | null;
  recordedAt?: string | null;
}

export interface SwapInput {
  thirdPlace?: string | null;
  fourthPlace?: string | null;
  /** The choosing order the draw produced, before any swap. */
  selectionOrder?: readonly string[];
  /** When the lottery was drawn. The option does not exist before it. */
  drawnAt?: string | null;
  /** When the option expires — the preference deadline (5:00 PM ET). */
  deadline?: string | null;
  declaration?: SwapDeclaration | null;
  now?: number;
}

const at = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

/** 1-based choosing position, or 0 for a GM who is not in the order. */
export function positionOf(order: readonly string[] | undefined, gm: string | null | undefined): number {
  if (!order || !gm) return 0;
  return order.findIndex((g) => g.toUpperCase() === gm.toUpperCase()) + 1;
}

/**
 * Whether the option exists at all.
 *
 * It needs both men, a draw, and both of them actually in the order. A league that did not record
 * a third and fourth place — or a draw that has not happened — has no option to be pending.
 */
export function swapAvailable(i: SwapInput): boolean {
  if (!i.thirdPlace || !i.fourthPlace) return false;
  if (!at(i.drawnAt)) return false;
  return positionOf(i.selectionOrder, i.thirdPlace) > 0
    && positionOf(i.selectionOrder, i.fourthPlace) > 0;
}

export function swapState(i: SwapInput): SwapState {
  if (!swapAvailable(i)) return 'pending';
  /*
   * A DECLARATION BEATS THE CLOCK, including one made after it. That is deliberate: a commissioner
   * recording a verbal answer given in the room at 4:58 may not type it until 5:10, and the answer
   * a man actually gave should not be overwritten by the fact that somebody was slow. `at` is when
   * he said it; `recordedAt` is when it was typed, and both are kept.
   */
  if (i.declaration) return i.declaration.answer;
  const deadline = at(i.deadline);
  const now = i.now ?? Date.now();
  if (deadline !== null && now >= deadline) return 'lapsed';
  return 'pending';
}

/** Whether the order should actually be swapped. Only an explicit `taken` does anything. */
export const swapExercised = (i: SwapInput): boolean => swapState(i) === 'taken';

/**
 * Exchange two GMs' choosing positions.
 *
 * A SWAP OF PLACES, NOT OF PEOPLE. The array is positional — index 3 is the fourth choice — so
 * this puts each man where the other stood and leaves everybody else exactly where they were.
 * Returns the input untouched when either man is missing, because a partial swap is worse than
 * none and this runs immediately before the resolve.
 */
export function swapPositions(
  order: readonly string[],
  thirdPlace: string,
  fourthPlace: string,
): string[] {
  const out = [...order];
  const a = positionOf(out, thirdPlace) - 1;
  const b = positionOf(out, fourthPlace) - 1;
  if (a < 0 || b < 0) return out;
  const tmp = out[a]!;
  out[a] = out[b]!;
  out[b] = tmp;
  return out;
}

/**
 * The choosing order the resolve should actually walk.
 *
 * Applied BEFORE `resolveDraftSlots`, which is the whole of the ruling: the swap changes whose
 * turn it is, and everything downstream — who gets his first choice, who is left with the
 * earliest free slot — falls out of that rather than being patched afterwards.
 */
export function orderAfterSwap(i: SwapInput): string[] {
  const order = [...(i.selectionOrder ?? [])];
  if (!swapExercised(i) || !i.thirdPlace || !i.fourthPlace) return order;
  return swapPositions(order, i.thirdPlace, i.fourthPlace);
}

/* ------------------------------------------------------------------------------ what to say */

export interface SwapPrompt {
  /** The question. */
  headline: string;
  /** "You drew 8th. Matt drew 1st." — and the warning line when swapping would cost him. */
  body: string;
  /** The button that SWAPS. Primary when it helps him, secondary when it does not. */
  take: { label: string; primary: boolean };
  /** The button that keeps his own draw. */
  keep: { label: string; primary: boolean };
  /** The consequence, stated once, under the buttons. */
  note: string;
  /** True when 3rd's own position is already better — the misclick case. */
  wouldMoveBack: boolean;
}

const ORD = (n: number) => {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

/**
 * The card 3rd place sees.
 *
 * ONE COMPONENT, BOTH DIRECTIONS. The rule offers the swap whichever way the draw fell, so the
 * card always offers it — but when 3rd already holds the better position the PRIMARY button
 * becomes Keep. Making the losing move the loud one is how a card that is technically correct
 * produces a misclick nobody can undo, and this declaration is binding.
 *
 * `counterpart` is named rather than called "fourth place" because on the night it is a person.
 */
export function swapPrompt(i: SwapInput, counterpartName?: string): SwapPrompt | null {
  if (!swapAvailable(i)) return null;
  const mine = positionOf(i.selectionOrder, i.thirdPlace);
  const theirs = positionOf(i.selectionOrder, i.fourthPlace);
  if (!mine || !theirs) return null;

  const them = counterpartName ?? 'fourth place';
  /* Lower number is earlier is better. Equal cannot happen — one order, two distinct men. */
  const wouldMoveBack = theirs > mine;

  return {
    /* The same question either way — what changes is which answer is the loud one. */
    headline: `Take ${them}’s lottery result?`,
    body: wouldMoveBack
      ? `You drew ${ORD(mine)}. ${them} drew ${ORD(theirs)}. Swapping would move you back.`
      : `You drew ${ORD(mine)}. ${them} drew ${ORD(theirs)}.`,
    take: { label: `Take ${ORD(theirs)}`, primary: !wouldMoveBack },
    keep: { label: `Keep ${ORD(mine)}`, primary: wouldMoveBack },
    note: `Binding once declared. ${them} moves to ${ORD(mine)} and has no say.`,
    wouldMoveBack,
  };
}

/** What 4th place is told while the option is live. */
export function counterpartNotice(
  i: SwapInput,
  thirdName?: string,
): string | null {
  if (swapState(i) !== 'pending' || !swapAvailable(i)) return null;
  const who = thirdName ?? 'Third place';
  return i.deadline
    ? `${who} may take your lottery result until ${etStamp(i.deadline)}.`
    : `${who} may take your lottery result.`;
}

/**
 * The standing item on 3rd's Home checklist.
 *
 * Silence-declines is the branch where he loses value he would almost always have taken, and the
 * reveal ends in a room full of people talking — exactly when a prompt gets dismissed. Home only
 * lists what is outstanding, so this disappears the moment he answers.
 */
export function swapOutstanding(i: SwapInput): { title: string; gm: string } | null {
  if (!swapAvailable(i) || swapState(i) !== 'pending' || !i.thirdPlace) return null;
  return {
    gm: i.thirdPlace,
    title: i.deadline
      ? `Take or decline the third-place swap — expires ${etStamp(i.deadline)}`
      : 'Take or decline the third-place swap',
  };
}

/** One line for the record, once it is settled. What September's argument will read. */
export function swapRecord(i: SwapInput, names?: (gm: string) => string): string {
  const name = names ?? ((g: string) => g);
  const state = swapState(i);
  if (!swapAvailable(i)) return 'No third-place option this year.';
  const third = name(i.thirdPlace!);
  const fourth = name(i.fourthPlace!);
  const d = i.declaration;
  const how = d?.declaredVia === 'verbal' && d.recordedBy
    ? ` Declared verbally, recorded by ${name(d.recordedBy)}.`
    : '';
  switch (state) {
    case 'taken':
      return `${third} took ${fourth}’s lottery result.${how}`;
    case 'declined':
      return `${third} declined the swap.${how}`;
    case 'lapsed':
      return `${third} did not answer by the deadline, so the option lapsed.`;
    default:
      return `${third} may still take ${fourth}’s lottery result.`;
  }
}
